import { CardGirl, OrderFooter } from 'components/OrderScreen';
import { TEXT_STRING } from '../../../../constants';
import { useCallback, useMemo, useState } from 'react';
import { EGameType, TAddGame, TConfirmGame, TProjectFemale } from 'types';
import { QuantitySelector, Table, Text } from 'components';
import GameTicketSelection, {
  validGameTickets,
  hasInvalidGameTickets,
} from './GameTicketSelection';
import { SHOW_SALON_GACHA_TICKETS } from '../../../../constants/featureFlags';
import { queryKeys } from '../../../../constants';
import { GachaTicketWallet } from 'types/gacha-tickets';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { addProjectGame } from 'api';
import { getFormattedAmount, validateGamePlayManyTime } from 'utils';
import { useChatStore, useUserStore } from 'store';
import { useToast } from 'hooks';

type Props = {
  females?: TProjectFemale[];
  selectedGame: TConfirmGame[];
  projectId: number;
  listSelectedGirl: TProjectFemale[];
  onSelectGirl: (female: TProjectFemale) => void;
  onUpdateQuantity: (id: number, quantity: number) => void;
  refetchFemales: () => void;
};

enum EGameTable {
  GAME = 'game',
  QUANTITY = 'quantity',
}

const chatStr = TEXT_STRING.CHAT;
const commonStr = TEXT_STRING.COMMON;

function ConfirmGame({
  females,
  selectedGame,
  projectId,
  listSelectedGirl,
  onSelectGirl,
  onUpdateQuantity,
  refetchFemales,
}: Props) {
  const { setActionChatStep } = useChatStore();
  const { toastError, toastSuccess } = useToast();
  const queryClient = useQueryClient();
  const userId = useUserStore((state) => state.user?.id);
  const [tickets, setTickets] = useState<
    NonNullable<TAddGame['gameGachaTickets']>
  >([]);
  const refreshTickets = useCallback(() => {
    if (SHOW_SALON_GACHA_TICKETS) {
      setTickets([]);
      void queryClient.invalidateQueries({
        queryKey: [queryKeys.SALON_GACHA_TICKETS, userId],
      });
    }
  }, [queryClient, userId]);

  const { mutateAsync: addGameMutate, isPending } = useMutation({
    mutationFn: (body: TAddGame) => {
      return addProjectGame(body);
    },
    onSuccess: refreshTickets,
    onError(error: any) {
      if (error?.response?.status === 409) refreshTickets();
      toastError(error);
    },
  });

  const listFemale = useMemo(() => {
    return (
      females?.filter((female) => {
        const isSingleSelectedGameSpecial = !selectedGame.some(
          (game) => game.id !== female.specialGameId,
        );
        const isGroupKing = female.specialGameType === EGameType.GROUP_KING;
        return !(isSingleSelectedGameSpecial || isGroupKing);
      }) || []
    );
  }, [females, selectedGame]);

  const listConfirmGirl = useMemo(() => {
    if (!listSelectedGirl.length) return listFemale;
    // 選択時点の情報は古くなり得るため、現在の対象候補に残っているBだけを送る
    return listSelectedGirl.filter((selected) =>
      listFemale.some((female) => female.id === selected.id),
    );
  }, [listFemale, listSelectedGirl]);

  const totalPriceGame = useMemo(() => {
    return selectedGame.reduce((sum, game) => {
      const isGameSpecial = !validateGamePlayManyTime(game.type);
      const quantityGame = game?.quantity ?? 0;

      const totalGirls = listConfirmGirl?.length || 0;
      let quantityGirl;

      if (isGameSpecial) {
        const totalGirlHasSpecialGame = listConfirmGirl.filter(
          (item) => item.specialGameId === game.id,
        ).length;

        quantityGirl = totalGirls - totalGirlHasSpecialGame;
      } else {
        quantityGirl = totalGirls;
      }

      return sum + quantityGame * game.price * quantityGirl;
    }, 0);
  }, [listConfirmGirl, selectedGame]);

  const handleAddGame = useCallback(async () => {
    try {
      if (!projectId || !selectedGame.length || listConfirmGirl.length === 0) return;
      const wallet = queryClient.getQueryData<GachaTicketWallet>([
        queryKeys.SALON_GACHA_TICKETS,
        userId,
      ]);
      if (SHOW_SALON_GACHA_TICKETS && hasInvalidGameTickets(tickets, wallet)) {
        refreshTickets();
        toastError(
          new Error(
            '選択した券が使えなくなりました。料金を確認して、もう一度追加してください。',
          ),
        );
        return;
      }
      await addGameMutate({
        ...(SHOW_SALON_GACHA_TICKETS
          ? {
              gameGachaTickets: validGameTickets(
                tickets,
                selectedGame,
                listConfirmGirl,
                wallet,
              ),
            }
          : {}),
        projectId: projectId,
        femaleIds: listConfirmGirl?.map((female) => female.user.id) || [],
        games: selectedGame
          .filter((game) => (game?.quantity || 0) > 0)
          .map((game) => {
            return { id: game.id, orderCount: game.quantity || 0 };
          }),
      });
      setActionChatStep();
      refetchFemales();
      toastSuccess(commonStr.SUCCESS);
    } catch {
      /* Error is shown by the mutation. */
    }
  }, [
    addGameMutate,
    tickets,
    queryClient,
    userId,
    listConfirmGirl,
    projectId,
    refetchFemales,
    selectedGame,
    setActionChatStep,
    toastSuccess,
    toastError,
    refreshTickets,
  ]);

  const listTable = useMemo(
    () =>
      selectedGame?.map((game) => {
        const quantity = game.quantity || 0;
        return {
          [EGameTable.GAME]: (
            <Text className="line-clamp-2 break-all">{game.name}</Text>
          ),
          [EGameTable.QUANTITY]: (
            <QuantitySelector
              className="justify-end gap-6"
              quantity={quantity}
              onIncrease={() => onUpdateQuantity(game.id, 1)}
              onDecrease={() => onUpdateQuantity(game.id, -1)}
              disabledIncrease={
                (!validateGamePlayManyTime(game.type) && quantity >= 1) ||
                isPending
              }
              disabledDecrease={isPending}
            />
          ),
        };
      }),
    [isPending, onUpdateQuantity, selectedGame],
  );

  return (
    <>
      <Text bold>{chatStr.GAMES_TO_ORDER}</Text>
      <div className="py-8">
        <Table
          columns={[
            { key: EGameTable.GAME, title: commonStr.GAME },
            {
              key: EGameTable.QUANTITY,
              title: <span className="pr-12">{commonStr.QUANTITY}</span>,
              align: 'right',
            },
          ]}
          data={listTable}
        />
      </div>
      <Text bold>{chatStr.SELECT_TECO_GIRL}</Text>
      <Text textColor="text-neutral">{chatStr.SELECT_NOTE}</Text>
      <div className="flex gap-2 mt-4">
        {listFemale?.map((female) => {
          return (
            <CardGirl
              key={female.id}
              onChoose={() => onSelectGirl(female)}
              onDetail={() => onSelectGirl(female)}
              data={female.user}
              isChoose={listSelectedGirl.some((item) => item.id === female.id)}
            />
          );
        })}
      </div>
      {SHOW_SALON_GACHA_TICKETS && (
        <GameTicketSelection
          games={selectedGame}
          females={listConfirmGirl}
          value={tickets}
          onChange={setTickets}
          disabled={isPending}
        />
      )}
      <OrderFooter
        onSubmit={handleAddGame}
        textButton={chatStr.ADD_GAME}
        className="mt-16px"
        loading={isPending}
        estimatePrice={
          <Text fontSize={14}>
            {getFormattedAmount(totalPriceGame)}
            {commonStr.YEN}
          </Text>
        }
        disabled={
          listConfirmGirl.length === 0 ||
          totalPriceGame < 0 ||
          selectedGame?.every((game) => (game.quantity || 0) <= 0)
        }
      />
    </>
  );
}

export default ConfirmGame;
